/**
 * ClinicianReviewPanel — clinician-facing review of a scored administration.
 *
 * Shows: the T-score profile chart, a validity-status banner, the list of
 * elevated scales, any safety events for the administration, and a
 * clinician-gated "Release results to patient" action. Includes the
 * provisional-norms disclaimer.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Lock,
  Unlock,
  RefreshCw,
  Loader2,
  Sparkles,
  Save,
  CheckCheck,
  BookOpenCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { TScoreProfileChart } from './TScoreProfileChart';
import { ProvisionalNormsDisclaimer } from './CrisisResources';
import {
  behavioralAssessmentApi,
  categoryLabel,
  type ClinicianSummary,
  type ProfileResult,
  type SafetyEvent,
  type ValidityStatus,
} from './types';

interface ClinicianReviewPanelProps {
  administrationId: string;
  /** Safety events relevant to this administration (optional; fetched if omitted). */
  safetyEvents?: SafetyEvent[];
  /** Invoked when the clinician releases results. No release endpoint exists in
   *  the documented contract yet — the parent decides how to persist this. */
  onRelease?: (administrationId: string) => void | Promise<void>;
}

const VALIDITY_STYLES: Record<
  ValidityStatus,
  { icon: React.ElementType; className: string; label: string; note: string }
> = {
  valid: {
    icon: ShieldCheck,
    className: 'border-green-300 bg-green-50 text-green-800',
    label: 'Valid profile',
    note: 'Response patterns fall within expected ranges.',
  },
  caution: {
    icon: ShieldAlert,
    className: 'border-amber-300 bg-amber-50 text-amber-800',
    label: 'Interpret with caution',
    note: 'One or more validity indicators are outside expected ranges.',
  },
  invalid: {
    icon: AlertTriangle,
    className: 'border-red-300 bg-red-50 text-red-800',
    label: 'Invalid profile',
    note: 'Validity indicators suggest this profile should not be interpreted.',
  },
};

export const ClinicianReviewPanel: React.FC<ClinicianReviewPanelProps> = ({
  administrationId,
  safetyEvents: safetyEventsProp,
  onRelease,
}) => {
  const [profile, setProfile] = useState<ProfileResult | null>(null);
  const [events, setEvents] = useState<SafetyEvent[]>(safetyEventsProp ?? []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [released, setReleased] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await behavioralAssessmentApi.getProfile(administrationId);
      setProfile(p);
      setReleased(p.status === 'released');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, [administrationId]);

  useEffect(() => {
    void load();
  }, [load]);

  // If the parent didn't hand us events, pull the open queue and filter.
  useEffect(() => {
    if (safetyEventsProp) {
      setEvents(safetyEventsProp);
      return;
    }
    let cancelled = false;
    behavioralAssessmentApi
      .listSafetyEvents('open')
      .then((res) => {
        if (!cancelled) {
          setEvents(
            res.events.filter((ev) => ev.administration_id === administrationId),
          );
        }
      })
      .catch(() => {
        /* non-fatal — the panel still renders without events */
      });
    return () => {
      cancelled = true;
    };
  }, [administrationId, safetyEventsProp]);

  const handleRelease = useCallback(async () => {
    if (!onRelease) return;
    setReleasing(true);
    try {
      await onRelease(administrationId);
      setReleased(true);
    } finally {
      setReleasing(false);
    }
  }, [administrationId, onRelease]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        Loading profile…
      </div>
    );
  }

  if (error || !profile) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
          <AlertTriangle className="h-6 w-6 text-red-500" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            {error ?? 'Profile unavailable.'}
          </p>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const validity = VALIDITY_STYLES[profile.validity_status] ?? VALIDITY_STYLES.caution;
  const ValidityIcon = validity.icon;
  const elevated = profile.scales.filter((s) => s.elevated);

  return (
    <div className="space-y-4">
      <ProvisionalNormsDisclaimer />

      {/* Validity banner */}
      <div
        role="status"
        className={`flex items-start gap-3 rounded-lg border p-4 ${validity.className}`}
      >
        <ValidityIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-semibold">{validity.label}</p>
          <p className="text-sm opacity-90">{validity.note}</p>
        </div>
      </div>

      {/* Safety events */}
      {events.length > 0 && (
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              Safety events ({events.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-medium text-red-800">{ev.trigger_type}</span>
                  {ev.item_number != null && (
                    <span className="text-red-600"> · item {ev.item_number}</span>
                  )}
                  <p className="text-red-700">{ev.detail}</p>
                </div>
                <Badge variant="destructive">{ev.severity}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Profile chart */}
      <Card>
        <CardHeader>
          <CardTitle>Score profile</CardTitle>
        </CardHeader>
        <CardContent>
          <TScoreProfileChart scales={profile.scales} thresholds={profile.thresholds} />
        </CardContent>
      </Card>

      {/* Elevated scales */}
      <Card>
        <CardHeader>
          <CardTitle>Elevated scales ({elevated.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {elevated.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No scales reached the elevated threshold (T &ge;{' '}
              {profile.thresholds.elevated}).
            </p>
          ) : (
            <ul className="space-y-2">
              {elevated
                .slice()
                .sort((a, b) => b.t_score - a.t_score)
                .map((s) => (
                  <li
                    key={s.scale_code}
                    className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                  >
                    <div>
                      <span className="font-medium">{s.scale_name}</span>{' '}
                      <span className="text-muted-foreground">
                        ({categoryLabel(s.category)})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{s.band}</Badge>
                      <span className="font-semibold tabular-nums">
                        T {Math.round(s.t_score)}
                      </span>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* AI draft summary (clinician only) */}
      <ClinicianDraftSummary administrationId={administrationId} />

      {/* Release gate */}
      <Card>
        <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            {released ? (
              <Unlock className="mt-0.5 h-4 w-4 text-green-600" aria-hidden="true" />
            ) : (
              <Lock className="mt-0.5 h-4 w-4" aria-hidden="true" />
            )}
            <span>
              {released
                ? 'Results have been released to the patient.'
                : 'Results are clinician-gated. The patient cannot see them until you release.'}
            </span>
          </div>
          <Button
            onClick={() => void handleRelease()}
            disabled={releasing || released || !onRelease}
          >
            {releasing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Unlock className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {released ? 'Released' : 'Release results to patient'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// AI draft summary (clinician only)
// ============================================================================

const DEFAULT_SUMMARY_DISCLAIMER =
  'AI-generated clinician draft. This is a working draft for the clinician only — ' +
  'it is never shown to the patient and is not a diagnosis. Review and edit before use.';

interface ClinicianDraftSummaryProps {
  administrationId: string;
}

const ClinicianDraftSummary: React.FC<ClinicianDraftSummaryProps> = ({
  administrationId,
}) => {
  const [summary, setSummary] = useState<ClinicianSummary | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState<false | 'save' | 'finalize'>(false);
  const [error, setError] = useState<string | null>(null);

  // Adopt a summary payload into local state (prefer edited_text over draft_text).
  const adopt = useCallback((s: ClinicianSummary | null) => {
    setSummary(s);
    setText(s ? s.edited_text ?? s.draft_text : '');
  }, []);

  // Fetch the latest summary on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    behavioralAssessmentApi
      .getClinicianSummary(administrationId)
      .then((res) => {
        if (!cancelled) adopt(res.summary);
      })
      .catch(() => {
        /* non-fatal — clinician can still generate a new draft */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [administrationId, adopt]);

  const generate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const s = await behavioralAssessmentApi.generateClinicianSummary(administrationId);
      adopt(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate draft.');
    } finally {
      setGenerating(false);
    }
  }, [administrationId, adopt]);

  const save = useCallback(
    async (finalize: boolean) => {
      setSaving(finalize ? 'finalize' : 'save');
      setError(null);
      try {
        const s = await behavioralAssessmentApi.saveClinicianSummary(administrationId, {
          edited_text: text,
          finalize,
        });
        adopt(s);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save.');
      } finally {
        setSaving(false);
      }
    },
    [administrationId, text, adopt],
  );

  const disclaimer = summary?.disclaimer || DEFAULT_SUMMARY_DISCLAIMER;
  const isFinalized = (summary?.status ?? '').toLowerCase() === 'finalized';

  return (
    <Card className="border-purple-200">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" aria-hidden="true" />
            AI draft summary (clinician only)
          </CardTitle>
          <div className="flex items-center gap-1">
            {summary?.rag_used && (
              <Badge variant="secondary" className="gap-1">
                <BookOpenCheck className="h-3 w-3" aria-hidden="true" />
                RAG-grounded
              </Badge>
            )}
            {summary?.model && <Badge variant="outline">{summary.model}</Badge>}
            {isFinalized && (
              <Badge variant="secondary" className="gap-1">
                <CheckCheck className="h-3 w-3" aria-hidden="true" />
                Finalized
              </Badge>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Clinician-only working draft. This is <strong>never shown to the patient</strong>{' '}
          and is <strong>not a diagnosis</strong>. Review and edit before use.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Prominent disclaimer */}
        <p
          role="note"
          className="rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900"
        >
          {disclaimer}
        </p>

        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading draft…
          </div>
        ) : !summary ? (
          <div className="flex flex-col items-start gap-3 py-2">
            <p className="text-sm text-muted-foreground">
              No draft yet. Generate a RAG-grounded draft summary to review.
            </p>
            <Button onClick={() => void generate()} disabled={generating}>
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Generate draft
            </Button>
          </div>
        ) : (
          <>
            <div>
              <label
                htmlFor={`clinician-summary-${administrationId}`}
                className="mb-1 block text-sm font-medium"
              >
                Draft (editable)
              </label>
              <Textarea
                id={`clinician-summary-${administrationId}`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                className="font-mono text-sm"
                aria-label="Clinician draft summary (editable)"
              />
              {summary.generated_at && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Generated {new Date(summary.generated_at).toLocaleString()}
                  {summary.edited_at
                    ? ` · edited ${new Date(summary.edited_at).toLocaleString()}`
                    : ''}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => void generate()}
                disabled={generating || saving !== false}
              >
                {generating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                Regenerate
              </Button>
              <Button
                variant="outline"
                onClick={() => void save(false)}
                disabled={saving !== false || generating}
              >
                {saving === 'save' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                Save edits
              </Button>
              <Button
                onClick={() => void save(true)}
                disabled={saving !== false || generating}
              >
                {saving === 'finalize' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <CheckCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                Save &amp; finalize
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
